// src/encounter/encounter-workflow.tsx
// MIDDLE column of the 3-column pre-encounter shell (Flutter PreEncounterWidget, encounter_screen.dart:1503).
// Card order mirrors Flutter body(): Reason+Start → Vitals → Chief Complaint → Special Form → Active Meds.
// Vitals sits BEFORE Chief Complaint (gap #3 vs the old single-column React order).
import { createElement, lazy, Suspense, type ReactNode, useMemo, useState } from 'react';
import {
  Button,
  ChiefComplaintField,
  MedicationActiveRow,
  StatusChip,
  type ActiveMedStatus,
  type VitalsRowVitals,
  type VitalsRowPatientType,
  VitalsHeader,
} from '@scripsteam/scrips-signal-ds';
import { MessageSquare, Plus, UserPlus } from 'lucide-react';
import { APPT_GLYPH, locationGlyph } from '@/scheduling/lib/appointment-glyphs';
import { SECTION_LABEL_CLASS, SECTION_LABEL_CLASS_V2 } from './pre-encounter-parts';
import { useDesignV2, V2_BODY_QUIET, V2_EYEBROW_HISTORY, V2_EYEBROW_MEDICATIONS, V2_META } from './lib/design-v2';
import { cn } from '@/lib/utils';
// The appointment panel's vitals cell + catalogue, reused as-is rather than re-drawn here.
// Cross-module on purpose: one editable vital cell in the repo, not two.
import { VitalCell, vitalCellHasValue } from '@/scheduling/components/view-appointment-panel/vitals/vital-cell';
import { filterCatalogForPatient } from '@/scheduling/components/view-appointment-panel/vitals/vital-catalog';
import { lmpIsMenstrualFrom } from '@/scheduling/components/view-appointment-panel/vitals/lmp-flags';
import { validateVitalsCodeForm } from './lib/to-vitals-request';
import { VITAL_CODES } from '@/types/vitals-api';
import type { PatientVital } from '@/types/vitals-api';
import { startActionLabel, type StartAction } from './lib/reason-for-visit';
import type { ReasonForVisit } from './pre-encounter-rail-view';
import { SubmittedQuestionnaireSection } from './submitted-questionnaire-section';
import type { SubmittedFormView } from './lib/to-submitted-questionnaire';
import { VaccineAdministrationRequests } from './vaccine-administration-requests';
import type { VaccineAdministrationRequestView } from './lib/to-vaccination-requests';
import { PreviousNotesSection } from './previous-notes-section';
import { NurseNotesSection, type NurseNotesView } from './nurse-notes-section';
import { ReferralCardSection } from './referral-card-section';
import type { ShellRole } from './lib/encounter-blocks';
import type { PreviousNoteView } from './lib/to-previous-notes';
import { MedicationAdminRequests } from './medication-admin-requests';
import type { MedicationAdminRequestView } from './lib/to-medication-admin-requests';
import { QuietLoading } from '@/shared/ui/components/quiet-loading';
import { EncBlock, EncSectionCard } from './enc-section-card';
import { useEncOverlayScope } from './enc-overlay-scope';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import type { NursePrevisitHost } from './nurse-previsit/nurse-previsit-template';
import { previsitDrawsReconciliation } from './lib/open-medication-reconciliation';

// Loaded only when the nurse's Signal pre-visit draws: it carries Antonin's package and its sheet.
const NursePrevisitTemplate = lazy(() =>
  import('./nurse-previsit/nurse-previsit-template').then((m) => ({ default: m.NursePrevisitTemplate })),
);

export interface ActiveMedicationItem {
  id: string;
  name: string;
  /** Dose — frequency — route line (GetPatientSummary-derived); the SIG line falls back to it. */
  detail?: string;
  /** Rich feeds (PROD-1588). SIG/dispense/refills/orderedFor ride GetPrescriptionOrderDetails;
   *  isRx/prescriber/dates ride GetPatientSummary — merged in use-prescription-orders.ts. */
  sig?: string;
  isRx?: boolean;
  status?: ActiveMedStatus;
  prescriber?: string;
  orderedOn?: string;
  orderedFor?: string;
  startedOn?: string;
  endedOn?: string;
  durationLabel?: string;
  dispense?: string;
  refills?: string;
}

export interface EncounterWorkflowProps {
  /** "Patient device data" (DeviceTrendsCard): its own section directly below the vitals (encounter owner, 1 Nov
   *  amendment to the 30 Oct wearables ruling). Never inside the vitals block. Absent = nothing drawn. */
  deviceData?: ReactNode;
  /** `ambientWindow` (B1): the ambient card, its own row above the pre-visit cards, right-aligned (the encounter's place). */
  ambientCard?: ReactNode;
  /** The nurse's pre-visit as Antonin designed it (nurse-previsit-template.tsx: his PreVisitPage, every slot fed by its adapter,
   *  the medication reconciliation included). Signal pre-visit, role nurse only: it replaces the hand-assembled cards below.
   *  The page supplies what only it knows (booking, visit, signed-in nurse). Absent = the cards as before. */
  nursePrevisit?: NursePrevisitHost;
  /** [PROD-2643] "Diagnostics and imaging" (Antonin's card, previsit-diagnostics-block.tsx): unreviewed results filed on or before the
   *  visit day. Signal pre-visit only, after the first card and before Previous notes (the design's order). */
  previsitDiagnostics?: ReactNode;
  reason: ReasonForVisit;
  /** The page header carries Start / Continue, Reschedule and the reason (pre-encounter-rail-view.tsx): this block draws none. */
  actionsInHeader?: boolean;
  startAction?: StartAction;
  startDisabledReason?: string;
  onStart?: () => void;
  onReschedule?: () => void;
  chiefComplaint: string;
  onChiefComplaintChange: (next: string) => void;
  ccSuggestions?: string[];
  ccSelected?: string[];
  onCcSelectionChange?: (next: string[]) => void;
  /** Commit the free-typed text as a complaint (the DS editor's "+ Create new" — Flutter's
   *  free-text tick button twin). Omitted → typed text is search-only. */
  onCcCreate?: (value: string) => void;
  /** Chief-complaint persistence is saving — the editor stays interactive; the saving note
   *  below the field keeps the write honest (no silent in-flight state). */
  ccSaving?: boolean;
  chiefComplaintDeferred?: boolean;
  /** Chief complaint is read-only for Booked / Pending / Proposed (Flutter's own rule). */
  chiefComplaintLocked?: boolean;
  vitals: VitalsRowVitals;
  patientType?: VitalsRowPatientType;
  onVitalsReviewed?: () => void;
  /** False → the "Vitals Reviewed" action renders disabled (nothing recorded on this
   *  encounter to review) instead of a silent no-op. Default true. */
  canReviewVitals?: boolean;
  onVitalsHistorical?: () => void;
  onVitalsEdit?: () => void;
  /** @deprecated WAVE 2 (Samer 2026-07-23): the "+ Visual Acuity" header affordance is
   *  retired — acuity capture lives in the Edit dialog. Prop kept (unused) so existing
   *  hosts/forwarders (rail-view) compile; remove on the next prop-surface cleanup. */
  onVisualAcuity?: () => void;
  /** Vitals reviewed-state (PROD-1536) — when true, a green check + "Reviewed" shows beside the
   *  Vitals title (Flutter vitalHeader() IconHelper.greenCheckIcon when state.isVitalReviewed). */
  vitalsReviewed?: boolean;
  /**
   * INLINE VITALS EDIT (Andrew 2026-08-08 — "like the edit appointment (calendar) -> vital tab").
   * The grid flips into edit mode in place: the `+` becomes Save / Cancel and the core cells swap
   * their value for an input. Absent → the grid stays read-only and the `+` falls back to
   * `onVitalsEdit` (the dialog), so hosts that have not wired this keep working.
   */
  onVitalsSubmit?: (form: Record<string, string>, lmpFlags?: { isMenstrual: boolean; isBreastFeeding: boolean }) => void;
  /** Catalogue gating — LMP is female>8, head circumference is age<=2 (same rule as the tab). */
  patientGender?: 'male' | 'female' | null;
  patientAge?: number | null;
  /** Existing readings — seed the form on entering edit mode. */
  vitalsExisting?: PatientVital[] | null;
  vitalsSaving?: boolean;
  /** PROD-2947 · frame v2 (Andrew's walk, 7 Oct, B12): a vitals save the server refused (the note is signed), said IN
   *  PLACE on the vitals header — not only in the note's banner. */
  vitalsNotice?: string;
  /**
   * The Visual Acuity capture form, rendered INLINE under the tile grid while editing (Flutter
   * pre_encounter_widget.dart vitals()). The host owns it because acuity persists through its
   * own `extraData` request, not the numeric form. Absent → nothing renders.
   */
  visualAcuity?: ReactNode;
  /** Cancel on the acuity panel — revert the draft to the last saved value (Flutter discards). */
  onAcuityCancel?: () => void;
  /** The acuity cell's OWN value — e.g. "20/20 · 20/25". Absent → the cell reads em-dash. */
  visualAcuitySummary?: string | null;
  /** Growth chart node (PROD-1538) — the page builds an age/sex-gated GrowthChart for minors
   *  (< 2y, known M/F) and passes it here; absent → nothing renders. Placed right after Vitals,
   *  mirroring Flutter, where the growth chart is an anthropometric extension of the vitals area. */
  growthChart?: ReactNode;
  /** Submitted-questionnaire view model (PROD-1251); null/absent → the section is hidden. */
  submittedForm?: SubmittedFormView | null;
  /** Submitted-QR fetch failed → show a note instead of silently hiding. */
  submittedFormError?: boolean;
  /** Patient first name — the not-yet-answered questionnaire copy (Flutter appointmentInfo()). */
  patientFirstName?: string;
  /** Vaccination administration requests (PROD-1250) — nested A/P vaccinations, derived by the host
   *  from the raw PatientAssessment. The section is hidden when empty (Flutter parity). */
  vaccinationRequests?: VaccineAdministrationRequestView[];
  /** Open the lot-aware administration editor for a request (PROD-1227 seam). Omitted → the rows
   *  render display-only (no action button). */
  onAdministerVaccination?: (request: VaccineAdministrationRequestView) => void;
  /** Previous completed encounter notes (follow-up only, PROD-1523). Empty/absent → hidden.
   *  Data source (a previous-encounter-notes hook) is a follow-up; threaded now so the wiring
   *  is a one-liner once it lands. */
  previousNotes?: PreviousNoteView[];
  onViewPreviousNote?: (note: PreviousNoteView) => void;
  /** [PROD-1395] The patient and the clinician's role, for the allied-health "Referral" card (flag `referrals`). */
  patientId?: string | null;
  encounterRole?: ShellRole;
  /** Medication administration requests (A/P meds, frequency=Now — PROD-1524). Empty → hidden.
   *  Display-only here; the "Give" write is a follow-up seam (like the vaccination editor). */
  medicationAdminRequests?: MedicationAdminRequestView[];
  onGiveMedication?: (request: MedicationAdminRequestView) => void;
  activeMedications?: ActiveMedicationItem[];
  /** Nurse pre-visit handoff (the E2 frame's "Notes from Nurse" block). Null/absent → hidden.
   *  NO React data feed exists yet (the risk-assessment module has no React port), so the page
   *  does not wire it — threaded now so the wiring is a one-liner once the feed lands. */
  nurseNotes?: NurseNotesView | null;
  /** `?x=nurse-note-card`: the card with its own data (nurse-notes-block.tsx), drawn where `nurseNotes` would be. */
  nurseNotesNode?: ReactNode;
  loading?: boolean;
}

/** The first-time/follow-up glyph next to the reason's label (Flutter reasonForVisit(): ScripsIcons.iconFirstTime/
 *  iconFollowUp). Not clickable, so a neutral 16 glyph with no fill (15 Oct, the canvas pass). */
const TYPE_CHIP_CLASS = 'inline-flex items-center justify-center text-text-secondary';
/** PreEncounter.dc.html primary action: `height:36; padding:0 32; border-radius:full; font 14/600`.
 *  Applied to the DS Button atom rather than replacing it — geometry only, no colour. */
// Published Flutter "Continue" pill: 104×39, full radius, 15px medium inverse text, 24px from
// the column's right edge (measured 1920 / zoom 100 % / DPR 1, PROD-1549).
const PILL_CLASS = 'h-10 -mt-px px-6 py-0 rounded-full text-size-body-15 font-medium';
/** Picked/suggested complaint chip — the design has no mock for these (its fixture is empty). */
const CC_CHIP_CLASS =
  'inline-flex items-center gap-2 py-0.75 px-3 rounded-full border border-solid border-border-default bg-grey-100 text-size-eyebrow font-sans text-text-secondary';
/** Design sheet v1.1 §5 (switch ON only): a picked complaint is a VALUE, so it reads text-primary
 *  (values lead, labels whisper). Geometry unchanged. */
const CC_CHIP_CLASS_V2 =
  'inline-flex items-center gap-2 py-0.75 px-3 rounded-full border border-solid border-border-default bg-grey-100 text-size-eyebrow font-sans text-text-primary';
/** Same chip, dashed + clickable — the suggestion variant. */
const CC_CHIP_SUGGESTION_CLASS =
  'inline-flex items-center gap-2 py-0.75 px-3 rounded-full border border-dashed border-border-default bg-grey-100 text-size-eyebrow font-sans text-text-secondary cursor-pointer';
/** Flutter Done/Cancel: 68x32, bold 15. Done = primary fill, Cancel = lightGrey + brand text. */
const ACUITY_BTN_CLASS =
  'h-8 min-w-17 px-4 py-0 inline-flex items-center justify-center border-0 rounded-md font-sans text-size-sm font-bold cursor-pointer';
const ACUITY_DONE_CLASS = `${ACUITY_BTN_CLASS} bg-brand-primary text-text-inverse`;
const ACUITY_CANCEL_CLASS = `${ACUITY_BTN_CLASS} bg-grey-100 text-brand-primary`;
/** The Chief Complaint card — CARD spread with boxShadow forced to none + its own gap/padding.
 *  PROD-1588 layering fix (Samer 2026-07-22 "too white — frames not clear"): the E2 cards
 *  carry Card shadow '2' elsewhere (shell/QR/wrap-up spec → --shadow-2, TOKEN-CONVERSION);
 *  this card is the one exception (no shadow — see the boxShadow override at its call site). */
const CHIEF_COMPLAINT_CARD_CLASS =
  'bg-surface-card border border-solid border-border-default rounded-lg shadow-none flex flex-col gap-2.75 pt-4 pr-3 pb-6.75 pl-4';
/** Design sheet v1.1 §1 Surfaces (switch ON only): the card lifts off the canvas — card border +
 *  `--shadow-2`. DCR-01 supersedes the 23 Sep `shadow-none` flattening above for the new look;
 *  OFF keeps it. Radius and padding are untouched (K1). */
const CHIEF_COMPLAINT_CARD_CLASS_V2 =
  'bg-surface-card border border-solid border-border-card rounded-lg shadow-2 flex flex-col gap-2.75 pt-4 pr-3 pb-6.75 pl-4';
/** The visual-acuity panel is an INSET (inside the vitals section): card border, no lift. */
const ACUITY_PANEL_CLASS = 'p-4 bg-surface-card border border-solid border-border-default rounded-md';
const ACUITY_PANEL_CLASS_V2 = 'p-4 bg-surface-card border border-solid border-border-card rounded-md';
const ACTIVE_MEDS_TITLE_CLASS =
  'm-0 text-size-eyebrow font-bold leading-normal tracking-section-heading uppercase text-blue-700';
const ACTIVE_MEDS_CARD_CLASS =
  'bg-surface-card border border-solid border-border-default rounded-card p-4 shadow-2 flex flex-col gap-4 w-full';
/** The reconciliation reminder under the Active Medications title. Switch ON: SYSTEM-RULES §1/§5 —
 *  meta in text-secondary; today's `text-hint` (#AFBAC0) is ~2:1 on white, under the 4.5:1 label
 *  floor. */
const ACTIVE_MEDS_REMINDER_CLASS = 'm-0 text-size-eyebrow font-medium leading-4 tracking-caption text-text-hint';
const ACTIVE_MEDS_REMINDER_CLASS_V2 = `m-0 leading-4 ${V2_META}`;
const ACTIVE_MEDS_CARD_CLASS_V2 =
  'bg-surface-card border border-solid border-border-card rounded-card p-4 shadow-2 flex flex-col gap-4 w-full';
/** Small hint/status note under the Chief Complaint field (deferred / saving). */
const HINT_NOTE_CLASS = 'text-size-xs text-text-hint';

export function EncounterWorkflow(props: EncounterWorkflowProps) {
  const {
    reason,
    actionsInHeader = false,
    startAction = 'start',
    startDisabledReason,
    onStart,
    onReschedule,
    chiefComplaint,
    onChiefComplaintChange,
    ccSuggestions,
    ccSelected,
    onCcSelectionChange,
    onCcCreate,
    ccSaving,
    chiefComplaintDeferred,
    chiefComplaintLocked,
    onVitalsReviewed,
    canReviewVitals,
    onVitalsHistorical,
    onVitalsEdit,
    vitalsReviewed,
    onVitalsSubmit,
    vitalsExisting,
    vitalsSaving,
    vitalsNotice,
    deviceData,
    ambientCard,
    nursePrevisit,
    previsitDiagnostics,
    patientGender,
    patientAge,
    visualAcuity,
    onAcuityCancel,
    growthChart,
    submittedForm,
    submittedFormError,
    patientFirstName,
    vaccinationRequests,
    onAdministerVaccination,
    previousNotes,
    onViewPreviousNote,
    patientId,
    encounterRole,
    medicationAdminRequests,
    onGiveMedication,
    activeMedications,
    nurseNotes,
    nurseNotesNode,
    loading,
  } = props;
  // Design sheet v1.1, switch ON only (`?ef=frameV2`); OFF renders today's classes.
  const designV2 = useDesignV2();
  // `?x=signal-previsit` (Frame V2 only): the pre-visit drawn to Antonin's catalog Section 3. Look only; read once per mount. The look is
  // `src/styles/showcase-previsit.css`, scoped to the `data-previsit="signal"` attribute set on the main column below.
  const [signalPrevisit] = useState(() => isEncounterFlagOn('signalPrevisit'));
  // `?x=template-review` (dev default ON): Antonin's 3 Nov review; here, the reason without its location pin (PROD-3393).
  const [templateReview] = useState(() => isEncounterFlagOn('templateReview'));
  // `?x=hide-previsit-meds` (OFF by default): the pre-visit Active medications list is not drawn, in either render.
  const showActiveMeds = !isEncounterFlagOn('hidePrevisitMeds');
  // `?x=previsitNoCc`: no pre-visit Chief complaint for a physician or a nurse; they enter it in the encounter's HPI step.
  // Allied health keeps it: it has no HPI step, and its sign still needs a chief complaint (the server refuses without one)
  // until a referral note carries the reason. Display only; the record, its save and the sign rule are unchanged.
  const hidePreVisitCc = isEncounterFlagOn('previsitNoCc') && (encounterRole === 'doctor' || encounterRole === 'nurse');
  // Frame v2: what opens over the pre-visit (dialogs, popovers) takes the encounter overlay look.
  useEncOverlayScope(designV2);
  const sectionLabelClass = designV2 ? SECTION_LABEL_CLASS_V2 : SECTION_LABEL_CLASS;
  // Inline vitals edit — the appointment panel's Vitals-tab model, reused wholesale (Andrew
  // 2026-08-08: "take all as they are from edit appointment -> vitals tab"). That tab's
  // `VitalCell` already ships the bespoke editors this grid was faking: Hearing as the SL/L/G
  // scale per ear, LMP as a date plus the menstruating/pregnancy/breast-feeding toggles, Visual
  // Acuity as the OS/OD pair, plus computed-cell read-only rules and the abnormal pill.
  // Its contract is a CODE-KEYED working copy, so the form is keyed by LOINC here too.
  const [vitalsEditingState, setVitalsEditing] = useState(false);
  const [vitalsForm, setVitalsForm] = useState<Record<string, string>>({});
  const [lmpIsMenstrual, setLmpIsMenstrual] = useState(true);
  // isBreastFeeding IS a wire field (vitals-api.ts:60) — Flutter never binds it, writing the
  // toggle to a device-global preference instead. We bind it to the patient record.
  const [lmpIsBreastFeeding, setLmpIsBreastFeeding] = useState(false);
  // Pregnancy indicator has NO wire field on either platform. Flutter keeps it in a GLOBAL
  // AppPreferenceUtil key (vital_cell_widget.dart:591), so it leaks across patients — a bug we
  // do not reproduce. Form-local here: it renders and toggles, but it does NOT persist, and a
  // backend field is needed before it can mean anything clinically.
  const [lmpIsPregnant, setLmpIsPregnant] = useState(false);
  const [vitalsError, setVitalsError] = useState<string | null>(null);
  // The LMP flags as the editor opened them (D-5101): a flag changed with no LMP value has no reading to ride on.
  const [lmpSeed, setLmpSeed] = useState<{ isMenstrual: boolean; isBreastFeeding: boolean }>({ isMenstrual: true, isBreastFeeding: false });
  // The acuity DETAIL grid is opened by its own `+` (the one thing the appointment tab lacks).
  const [acuityOpen, setAcuityOpen] = useState(false);
  const canEditVitalsInline = !!onVitalsSubmit;
  // B12 (Andrew's walk, 7 Oct): an editor open when the write closes (the provider signs) closes with it — read-only.
  const vitalsEditing = vitalsEditingState && canEditVitalsInline;
  const vitalCatalog = useMemo(
    () => filterCatalogForPatient(patientGender ?? null, patientAge ?? null),
    [patientGender, patientAge],
  );
  // Flutter's pre-encounter cell prints a unit only where it is a short SYMBOL; the three
  // abbreviations it carries in the model (BPM / mmHg / RPM, ui_data.dart:1184+) are not drawn
  // on this screen. Andrew 2026-08-09 ("remove the not exists ones"). Kept here rather than in
  // the shared cell so the appointment panel keeps its own display rule.
  const UNITLESS_ON_PRE_ENCOUNTER = new Set<string>([
    VITAL_CODES.HEART_RATE,
    VITAL_CODES.SYSTOLIC,
    VITAL_CODES.DIASTOLIC,
    VITAL_CODES.RESP_RATE,
  ]);
  const readingByCode = useMemo(() => {
    const m = new Map<string, PatientVital>();
    for (const r of vitalsExisting ?? []) {
      const c = r.code?.code;
      if (c && !r.isDeleted) m.set(c, r);
    }
    return m;
  }, [vitalsExisting]);
  // NEVER AN EMPTY BOX (switch ON; Samer 1 Oct: "the vitals are very bad"; the readout rule of
  // PROD-2816). At rest the grid draws only the tiles that carry a reading, then ONE quiet line
  // counting the rest; with none recorded it is one honest line under the header, whose `+` and
  // "View Vitals" stay. Editing draws every tile, because that is where a missing value is typed.
  // "Recorded" is the cell's own rule (`vitalCellHasValue`), so exactly the "—" tiles go.
  // OFF: the full catalogue, as today.
  const hideEmptyVitals = designV2 && !vitalsEditing;
  const shownVitals = hideEmptyVitals
    ? vitalCatalog.filter((entry) =>
        vitalCellHasValue(entry, readingByCode.get(entry.code) ?? null, vitalsExisting ?? []),
      )
    : vitalCatalog;
  const vitalsNotRecorded = vitalCatalog.length - shownVitals.length;
  // Re-seed on every entry so cancel-then-reopen picks up whatever landed since.
  function beginVitalsEdit() {
    const seed: Record<string, string> = {};
    for (const entry of vitalCatalog) {
      seed[entry.code] = readingByCode.get(entry.code)?.quantity?.value ?? '';
    }
    setVitalsForm(seed);
    const lmpRow = readingByCode.get(VITAL_CODES.LMP);
    // D-5101: a stored "Not menstruating" with no flag opens as not menstruating (lmpIsMenstrualFrom).
    const flags = { isMenstrual: lmpIsMenstrualFrom(lmpRow), isBreastFeeding: lmpRow?.isBreastFeeding ?? false };
    setLmpIsMenstrual(flags.isMenstrual);
    setLmpIsBreastFeeding(flags.isBreastFeeding);
    setLmpSeed(flags);
    setVitalsError(null);
    setVitalsEditing(true);
  }
  function cancelVitalsEdit() {
    setVitalsEditing(false);
    setVitalsError(null);
    setAcuityOpen(false);
  }
  function submitVitals() {
    const err = validateVitalsCodeForm(vitalsForm);
    if (err) {
      setVitalsError(err);
      return;
    }
    // D-5101: the LMP flags are saved on the LMP reading, and a blank LMP sends no reading, so a changed flag would be
    // lost without a word. Say what is missing instead. (No LMP cell for this patient: no key, no check.)
    const lmpValue = vitalsForm[VITAL_CODES.LMP];
    const lmpFlagsChanged = lmpIsMenstrual !== lmpSeed.isMenstrual || lmpIsBreastFeeding !== lmpSeed.isBreastFeeding;
    if (lmpValue !== undefined && !lmpValue.trim() && lmpFlagsChanged) {
      setVitalsError('Add the LMP date, or turn on “No longer menstruating”, to save the LMP indicators.');
      return;
    }
    setVitalsError(null);
    onVitalsSubmit?.(vitalsForm, { isMenstrual: lmpIsMenstrual, isBreastFeeding: lmpIsBreastFeeding });
    setVitalsEditing(false);
    setAcuityOpen(false);
  }

  // Per-med expanded order-detail state (Flutter isCollapsed, active_medication_row_widget.dart:53).
  const [expandedMeds, setExpandedMeds] = useState<Record<string, boolean>>({});
  const visitTypeLabel =
    reason.appointmentType === 'follow-up'
      ? 'Follow-up visit'
      : reason.appointmentType === 'first'
        ? 'First visit'
        : undefined;

  const vitalsBodyEl = (
    <>
      {/* B12: a refused save is said here, under the vitals header — not only in the note's banner. */}
      {vitalsNotice ? (
        <p role="status" className="m-0 text-size-sm text-status-error">
          {vitalsNotice}
        </p>
      ) : null}
      {loading ? (
        // Signal pre-visit: the skeleton bars are drawn on the Vitals block's loading slot only (showcase-previsit.css).
        signalPrevisit ? (
          <div data-slot="pv-vitals-loading">
            <QuietLoading label="Loading vitals..." />
          </div>
        ) : (
          <QuietLoading label="Loading vitals..." />
        )
      ) : hideEmptyVitals && shownVitals.length === 0 ? (
        <p className={`m-0 ${V2_BODY_QUIET}`}>No vitals recorded yet for this visit.</p>
      ) : (
        <>
        <div
          // Published Flutter (PROD-1549, 1920 / zoom 100 % / DPR 1): three columns, 9px gap —
          // 3 × 321 + 2 × 9 fills the column's 981px inner width exactly.
          // Flutter: rows 61 tall on a 69 pitch, columns 321 on a 330 pitch
          className="grid grid-cols-3 gap-2"
          data-slot="pv-vitals-grid"
        >
          {shownVitals.map((entry) => {
            const cell = (
              <VitalCell
                entry={entry}
                compact
                hideUnit={UNITLESS_ON_PRE_ENCOUNTER.has(entry.code)}
                hearingVariant="bar"
                reading={readingByCode.get(entry.code) ?? null}
                allReadings={vitalsExisting ?? []}
                editing={vitalsEditing}
                formValues={vitalsForm}
                onValueChange={(code, next) => setVitalsForm((f) => ({ ...f, [code]: next }))}
                lmpIsMenstrual={lmpIsMenstrual}
                onLmpMenstrualChange={setLmpIsMenstrual}
                lmpShowIndicators
                lmpIsBreastFeeding={lmpIsBreastFeeding}
                onLmpBreastFeedingChange={setLmpIsBreastFeeding}
                lmpIsPregnant={lmpIsPregnant}
                onLmpPregnantChange={setLmpIsPregnant}
                historicalReadings={vitalsExisting ?? []}
              />
            );
            // VISUAL ACUITY is the ONE thing the appointment tab doesn't carry: its cell edits
            // the OS/OD summary only, and the full distance/near x sc/cc/ph grid (which writes
            // `extraData`) has no entry point there. This `+` is that entry point — a sibling of
            // the cell, not a replacement for it, so the cell keeps the tab's own anatomy.
            if (entry.code !== VITAL_CODES.VISUAL || !visualAcuity) {
              return <div key={entry.code}>{cell}</div>;
            }
            return (
              <div key={entry.code} className="relative">
                {cell}
                {vitalsEditing ? (
                  <button
                    type="button"
                    onClick={() => setAcuityOpen((v) => !v)}
                    aria-expanded={acuityOpen}
                    aria-label={acuityOpen ? 'Hide visual acuity details' : 'Add visual acuity values'}
                    // The app icon button with Lucide's plus (icon pass, 16 Oct; was a 26 box with the DS 1.5 px plus).
                    className="icon-action absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer"
                  >
                    <Plus className="size-4" aria-hidden />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
        {hideEmptyVitals && vitalsNotRecorded > 0 ? (
          <p className={`m-0 ${V2_META}`}>{vitalsNotRecorded} not recorded</p>
        ) : null}
        </>
      )}
      {/* VISUAL ACUITY - the "another control to capture the details" Flutter renders INLINE in
          vitals() under the tile grid, gated on isEditing (pre_encounter_widget.dart:241-252).
          It is the FULL multi-mode grid (distance/near x left/right x sc/cc/ph_sc/ph_cc) that
          writes `extraData` - not the simplified OS/OD pair the appointment panel's inline
          editor ships (see visual-editor.tsx's own note: "the PA-side modal populates the full
          grid"). Hidden at rest, exactly as Flutter hides it outside edit mode. */}
      {vitalsEditing && acuityOpen && visualAcuity ? (
        <div
          className={designV2 ? ACUITY_PANEL_CLASS_V2 : ACUITY_PANEL_CLASS}
        >
          <span className={`${sectionLabelClass} block mb-3`}>
            Add Visual Acuity Values
          </span>
          {visualAcuity}
          {/* Flutter buttonRow() (visual_acuity_form.dart:208-440): Done 68x32 in the primary
              fill, Cancel 68x32 in lightGrey. BOTH close the panel — Done keeps what was typed
              (it persists with the vitals Save, the one write), Cancel discards it by reverting
              the draft, which is Flutter's copyWith(isAddVisualVitalsClicked:false) with no
              commit. */}
          <div className="flex gap-6 mt-4">
            <button type="button" onClick={() => setAcuityOpen(false)} className={ACUITY_DONE_CLASS}>
              Done
            </button>
            <button
              type="button"
              onClick={() => {
                onAcuityCancel?.();
                setAcuityOpen(false);
              }}
              className={ACUITY_CANCEL_CLASS}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </>
  );

  const ccBodyEl = (
    <>
      <div inert={chiefComplaintDeferred || undefined} className={cn(chiefComplaintDeferred && 'opacity-55')}>
        <ChiefComplaintField
          value={chiefComplaint}
          onChange={onChiefComplaintChange}
          disabled={chiefComplaintDeferred || chiefComplaintLocked}
          // Flutter shows the pencil at rest, before anything is typed; it commits only once
          // there is text, so an empty field makes it a no-op rather than hiding it.
          onEdit={
            onCcCreate
              ? () => {
                  const next = chiefComplaint.trim();
                  if (next) onCcCreate(next);
                }
              : undefined
          }
        />
        {/* Picked complaints — real data the design's empty mock never had to show. */}
        {(ccSelected?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {(ccSelected ?? []).map((name) => (
              <span key={name} className={designV2 ? CC_CHIP_CLASS_V2 : CC_CHIP_CLASS}>
                {name}
                {onCcSelectionChange && (
                  <button
                    type="button"
                    onClick={() => onCcSelectionChange((ccSelected ?? []).filter((n) => n !== name))}
                    aria-label={`Remove ${name}`}
                    className="border-0 bg-transparent p-0 cursor-pointer text-inherit leading-none"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        {/* Suggestions appear only while typing — invisible at rest, so the design holds. */}
        {(ccSuggestions?.length ?? 0) > 0 && chiefComplaint.trim().length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {(ccSuggestions ?? [])
              .filter((sug) => !(ccSelected ?? []).includes(sug))
              .map((sug) => (
                <button
                  key={sug}
                  type="button"
                  onClick={() => onCcSelectionChange?.([...(ccSelected ?? []), sug])}
                  className={CC_CHIP_SUGGESTION_CLASS}
                >
                  {sug}
                </button>
              ))}
          </div>
        )}
      </div>
      {chiefComplaintDeferred ? (
        <span className={HINT_NOTE_CLASS}>
          Available once the encounter is created.
        </span>
      ) : ccSaving ? (
        <span className={HINT_NOTE_CLASS}>Saving…</span>
      ) : null /* Flutter draws nothing under the field at rest (PROD-1549). */}
    </>
  );

  const activeMedRowsEl = (
    <>
      {(activeMedications ?? []).map((m) => {
          // Detail block exists when any order/summary rich field is present — only then does
          // the Expand control render (Flutter canExpand, active_medication_row.dart:66).
          const hasDetail = Boolean(
            m.sig || m.orderedFor || m.orderedOn || m.startedOn || m.endedOn || m.dispense || m.refills,
          );
          return (
            <MedicationActiveRow
              key={m.id}
              med={{
                name: m.name,
                // Collapsed SIG line: the order's Patient SIG when it exists, else the
                // dose — frequency — route derivation (both are "instructions" renderings).
                sig: m.sig || m.detail || '',
                // Rx chip (Flutter IconHelper.rxIcon when isOrder).
                medClass: m.isRx ? 'Rx' : undefined,
                prescriber: m.prescriber,
                startedOn: m.startedOn,
                endedOn: m.endedOn,
                durationLabel: m.durationLabel,
                // Expanded block: keep the dose line distinct from the SIG only when a real
                // SIG exists (otherwise the sig line above already shows it).
                dose: m.sig ? m.detail : undefined,
                dispense: m.dispense,
                refills: m.refills,
                orderedFor: m.orderedFor,
                orderedOn: m.orderedOn,
              }}
              status={m.status ?? 'active'}
              // No onTaking/onNotTaking (no server endpoint — see block comment) and no
              // Change/Refill/Stop (Flutter pre-encounter passes showEditOption=false too).
              expanded={Boolean(expandedMeds[m.id])}
              onToggleExpand={
                hasDetail
                  ? () => setExpandedMeds((prev) => ({ ...prev, [m.id]: !prev[m.id] }))
                  : undefined
              }
            />
          );
        })}
    </>
  );

  // FRAME V2 · the pre-visit step body as the design draws it (export 21-10, EncPreVisitMain + EncCorePreVisitSections): one
  // Objective card of blocks (Reason for visit, Vitals, Growth chart, Chief complaint, Notes from nurse), then one card per
  // section under it. Same data, same handlers and the same vitals editor as the OFF render; only the drawing changes. The root
  // carries `pa-pre-encounter-main` so the app-wide type rules skip it and the encounter one-look sheet reaches it.
  function renderPreVisitV2() {
    const reviewAction = vitalsReviewed ? (
      <StatusChip semantic="success" tone="soft" title="Vitals reviewed" aria-label="Vitals reviewed">
        ✓ Reviewed
      </StatusChip>
    ) : onVitalsReviewed ? (
      <button
        type="button"
        data-slot="pv-review"
        onClick={canReviewVitals === false ? undefined : onVitalsReviewed}
        aria-disabled={canReviewVitals === false}
        aria-label={
          canReviewVitals === false
            ? 'Mark vitals reviewed - unavailable: no vitals recorded on this encounter yet'
            : 'Mark vitals reviewed'
        }
        title={canReviewVitals === false ? 'No vitals recorded on this encounter yet' : 'Mark vitals reviewed'}
      >
        <StatusChip semantic="neutral" tone="soft">
          Mark reviewed
        </StatusChip>
      </button>
    ) : null;
    const onAddVitals = canEditVitalsInline ? beginVitalsEdit : onVitalsEdit;
    const vitalsActions = vitalsEditing ? (
      <>
        {vitalsError ? (
          <span data-slot="pv-vitals-error" role="alert">
            {vitalsError}
          </span>
        ) : null}
        <Button variant="tertiary" size="sm" onClick={cancelVitalsEdit} disabled={vitalsSaving}>
          Cancel
        </Button>
        <Button size="sm" onClick={submitVitals} disabled={vitalsSaving}>
          {vitalsSaving ? 'Saving…' : 'Save'}
        </Button>
      </>
    ) : (
      <>
        {reviewAction}
        {onVitalsHistorical ? (
          <Button variant="tertiary" size="sm" onClick={onVitalsHistorical}>
            History
          </Button>
        ) : null}
        {onAddVitals ? (
          <Button variant="tertiary" size="sm" onClick={onAddVitals}>
            Edit
          </Button>
        ) : null}
      </>
    );
    const startActions = !actionsInHeader ? (
      <>
        {onReschedule && (
          <Button variant="secondary" size="sm" onClick={onReschedule}>
            Reschedule
          </Button>
        )}
        <Button size="sm" onClick={onStart} disabled={Boolean(startDisabledReason)}>
          {startActionLabel(startAction)}
        </Button>
      </>
    ) : undefined;
    const meds = activeMedications ?? [];
    const reasonBody = (
      <>
        <div data-slot="pv-reason-row">
          {/* PROD-3393 (Antonin, 3 Nov: "remove the pin icon"): with the review on, the signal pre-visit's reason has no location glyph. */}
          {reason.locationType && !(signalPrevisit && templateReview) && (
            <span data-slot="pv-reason-glyph" aria-label={`${reason.locationType} visit`}>
              {createElement(locationGlyph(reason.locationType), { className: 'size-6', 'aria-hidden': true })}
            </span>
          )}
          {reason.appointmentType === 'follow-up' || reason.appointmentType === 'first' ? (
            <StatusChip semantic="neutral">{reason.appointmentType === 'first' ? 'First visit' : 'Follow-up'}</StatusChip>
          ) : null}
          <span data-slot="pv-reason-text">{reason.reason || 'No reason recorded'}</span>
        </div>
        {reason.comment ? <p data-slot="pv-reason-comment">{reason.comment}</p> : null}
        {!actionsInHeader && startDisabledReason ? <p data-slot="pv-start-reason">{startDisabledReason}</p> : null}
      </>
    );
    // The nurse's pre-visit is Antonin's page (nurse-previsit-template.tsx). What is not in his frame stays under it, as today, in
    // the old path's order: the growth chart (paediatric, PROD-2044), the device data (1 Nov amendment), then the nurse's own orders
    // to carry out (medicines and vaccines to give). Start is the page step bar's only (Antonin, 15 Oct), never on his reason card.
    // A refused vitals save (B12) is said on his Vitals card (vitalsNotice).
    if (nursePrevisit && previsitDrawsReconciliation({ signalPrevisit, encounterRole, hasNurseTemplate: true })) {
      return (
        <main data-slot="pa-pre-encounter-main" data-previsit="nurse-template">
          <div data-slot="pv-canvas">
            {/* `ambientWindow` (PR 904): the ambient card's own row above the page, as on the other pre-visit. */}
            {ambientCard ? (
              <div data-slot="ambient-card-row" className="flex justify-end">
                {ambientCard}
              </div>
            ) : null}
            <Suspense fallback={null}>
              <NursePrevisitTemplate
                {...nursePrevisit}
                patientId={patientId}
                encounterReason={reason.reason}
                vitalsNotice={vitalsNotice}
                nurseNotes={nurseNotes}
                nurseNoteNode={nurseNotesNode}
                patientGender={patientGender}
                patientAge={patientAge}
                onVitalsHistory={onVitalsHistorical}
                onVitalsEdit={onVitalsEdit}
                onVitalsSave={onVitalsSubmit ? (form) => onVitalsSubmit(form) : undefined}
                vitalsSaving={vitalsSaving}
                submittedForm={submittedForm}
                submittedFormError={submittedFormError}
                patientFirstName={patientFirstName}
                previousNotes={previousNotes}
                onViewPreviousNote={onViewPreviousNote}
              />
            </Suspense>
            {growthChart && <section aria-label="Growth chart">{growthChart}</section>}
            {deviceData}
            <MedicationAdminRequests requests={medicationAdminRequests ?? []} onGive={onGiveMedication} />
            <VaccineAdministrationRequests requests={vaccinationRequests ?? []} onAdminister={onAdministerVaccination} />
          </div>
        </main>
      );
    }
    return (
      <main data-slot="pa-pre-encounter-main" data-previsit={signalPrevisit ? 'signal' : undefined}>
        <div data-slot="pv-canvas">
          {ambientCard ? (
            <div data-slot="ambient-card-row" className="flex justify-end">
              {ambientCard}
            </div>
          ) : null}
          <EncSectionCard section="o" aria-label="Pre-visit">
            <EncBlock title="Reason for visit" icon="hpi" section="s" aria-label="Reason for visit" actions={startActions}>
              {/* Signal pre-visit: the reason, its comment and the start reason sit in one thin grey inner card (catalog ReasonCard). */}
              {signalPrevisit ? <div data-slot="pv-reason-card">{reasonBody}</div> : reasonBody}
            </EncBlock>
            {/* Catalog card 1 order: Reason for visit, Nurse note, Vitals (PvReason, PvNurseNote, PvVitalsCard). Today's order keeps the nurse's notes last. */}
            {signalPrevisit ? (nurseNotesNode ?? <NurseNotesSection notes={nurseNotes} />) : null}
            <EncBlock title="Vitals" icon="vitals" aria-label="Vitals" actions={vitalsActions}>
              {vitalsBodyEl}
              {/* The growth chart (paediatric only) follows the vitals in the same Objective block, untitled: the DS chart draws
                  its own header row, so a block title would repeat it (PROD-2044). The design draws no growth chart. */}
              {growthChart && <section aria-label="Growth chart">{growthChart}</section>}
            </EncBlock>
            {deviceData}
            {!hidePreVisitCc && (
              <EncBlock title="Chief complaint" icon="hpi" section="s" aria-label="Chief complaint">
                <div data-slot="pv-cc">
                  {ccBodyEl}
                </div>
              </EncBlock>
            )}
            {signalPrevisit ? null : (nurseNotesNode ?? <NurseNotesSection notes={nurseNotes} />)}
          </EncSectionCard>
          {signalPrevisit ? previsitDiagnostics : null}
          <SubmittedQuestionnaireSection form={submittedForm} error={submittedFormError} patientFirstName={patientFirstName} />
          <PreviousNotesSection notes={previousNotes ?? []} onView={onViewPreviousNote} />
          <MedicationAdminRequests requests={medicationAdminRequests ?? []} onGive={onGiveMedication} />
          <VaccineAdministrationRequests requests={vaccinationRequests ?? []} onAdminister={onAdministerVaccination} />
          {showActiveMeds && meds.length > 0 ? (
            // The design draws no active medications; drawn in its language (one Plan card, one block, the reminder as the
            // block's sub line), the same DS rows and expand handler as the OFF render.
            <EncSectionCard section="p" aria-label="Active medications">
              <EncBlock
                title="Active medications"
                icon="pill"
                sub="Remember to do medication reconciliation before you start the encounter."
              >
                <div data-slot="pv-meds">{activeMedRowsEl}</div>
              </EncBlock>
            </EncSectionCard>
          ) : null}
        </div>
      </main>
    );
  }

  if (designV2) return renderPreVisitV2();

  return (
    <main
      // Published Flutter main column (PROD-1549, measured 1920 / zoom 100 % / DPR 1): a WHITE
      // column (Flutter paints it; the page grey shows only behind the rail), content inset 17
      // (vitals tiles at x 402 in a column starting at 385), RFV eyebrow cap 28px below the strip.
      // Flutter: eyebrow cap 31px below the strip → 28px top inset for an 11/13 line.
      // Flutter web draws overlay scrollbars — no gutter — so the column stays 1015 wide.
      className="flex-1 min-w-0 flex flex-col gap-6 p-6 pb-10 bg-surface-card overflow-y-auto scrollbar-hide"
    >
      {/* Reason for visit + Start/Reschedule.
          LONGEVITY COMPOSITION (PreEncounter.dc.html, Andrew 2026-08-07): a FRAMELESS strip, not
          a card — label row + oversized reason + a right-aligned pill. The card/DS-section-header
          treatment is gone; Flutter agrees (reasonForVisit() is a bare Row with no decoration,
          pre_encounter_widget.dart:325). */}
      <section
        // Flutter: the RFV block sits 26px into the column (9 beyond the 17px column inset) and
        // the pill ends 24px from the column's right edge (7 inside the inset).
        // Flutter: VITALS cap sits 54px under the headline cap bottom — 22px more than the column gap.
        className="flex items-start gap-4"
        aria-label="Reason for visit"
      >
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span
              className="text-xs font-medium text-text-secondary"
            >
              Reason for visit
            </span>
            {/* The chip carries the VISIT TYPE (Flutter reasonForVisit()'s lightGrey 16px box +
                iconFirstTime/iconFollowUp). The design's "1" is NOT a count — the DS
                `appt-first-time` glyph literally draws a figure-1 (verified in the live DOM), so
                the design was reproducing the glyph. Follow-up renders its own curved-arrow
                glyph, which is why this stays an Icon rather than a hardcoded numeral. Icon pass (16 Oct): Lucide,
                the follow-up the appointment glyph map's CalendarPlus, a first visit UserPlus (a new patient). */}
            {reason.appointmentType && (
              <span
                className={TYPE_CHIP_CLASS}
                title={visitTypeLabel}
                aria-label={visitTypeLabel}
              >
                {reason.appointmentType === 'follow-up' ? (
                  <APPT_GLYPH.followUp className="size-4" aria-hidden />
                ) : (
                  <UserPlus className="size-4" aria-hidden />
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Modality tile — Flutter draws an 18×18 filled brand square at x 412 with the headline
                starting at x 441 (measured 1920 / zoom 100 % / DPR 1). The glyph inside stays the
                modality glyph (icon semantics are product's, B1). Hidden when the modality is unknown. */}
            {reason.locationType && (
              <span
                className="inline-flex items-center justify-center shrink-0 text-text-secondary"
                aria-label={`${reason.locationType} visit`}
              >
                <ReasonLocationGlyph type={reason.locationType} />
              </span>
            )}
            <span
              // The value of a label/value pair (15 Oct, the canvas pass; the prototype's chief-complaint line): body
              // strong in primary ink. The page title is the patient's name, so the reason is not a second title.
              className="min-w-0 text-sm font-medium text-text-primary text-pretty"
            >
              {reason.reason || 'No reason recorded'}
            </span>
          </div>
          {reason.comment && (
            /* Flutter: a comment glyph precedes the red appointment comment. */
            <p
              className="flex items-center gap-2 mt-1 mx-0 mb-0 text-size-sm text-status-error"
            >
              <MessageSquare className="size-4 shrink-0" aria-hidden />
              {reason.comment}
            </p>
          )}
        </div>
        {!actionsInHeader && (
          <div className="flex flex-col items-end gap-1">
            {/* Start + Reschedule sit side by side (Flutter reasonForVisit() Row). The DS Button
                atom is still the control — only its geometry follows the design's 36px pill
                (`height:36; padding:0 32; border-radius:full`), via tokens. */}
            <div className="flex gap-2">
              {/* Greyed, not removed, when the appointment status forbids starting — Flutter's own
                  behaviour (`disabledBtnBGColor`, pre_encounter_widget.dart:415-425). The reason
                  renders below. */}
              <Button onClick={onStart} disabled={Boolean(startDisabledReason)} className={PILL_CLASS}>
                {startActionLabel(startAction)}
              </Button>
              {onReschedule && (
                <Button variant="secondary" onClick={onReschedule} className={PILL_CLASS}>
                  Reschedule
                </Button>
              )}
            </div>
            {startDisabledReason && (
              <p
                className="m-0 text-size-xs text-text-hint max-w-55 text-right"
              >
                {startDisabledReason}
              </p>
            )}
          </div>
        )}
      </section>

      {/* VITALS - the Claude Design HANDOFF spec (project 22239bcb..., handoff/
          VitalsHistoryInPreEncounter.module.css), which supersedes the earlier .dc.html read:
            .vitalsHead  label - spacer - reviewed check - "View Vitals" - the INLINE 26px add
            .vitalsGrid  repeat(3, 1fr), gap 10
            .vCell       one fixed anatomy (32px DS VitalIcon + label-over-value), so every
                         cell is the same height.
          "Add vitals" is a HEADER affordance here, per the design - it opens the same editor
          the Edit action used to. The catalogue still comes from `preEncounterVitalSpecs`, so
          this render and the in-encounter strip cannot drift. */}
      <section aria-label="Vitals" className="flex flex-col gap-2">
        <VitalsHeader
          reviewed={vitalsReviewed}
          onToggleReviewed={onVitalsReviewed}
          canReview={canReviewVitals}
          onViewVitals={onVitalsHistorical}
          // The `+` opens INLINE edit when the host wired a submit; otherwise it falls back to
          // the Edit dialog, so nothing regresses for hosts that haven't adopted it.
          onAdd={canEditVitalsInline ? beginVitalsEdit : onVitalsEdit}
          editing={vitalsEditing}
          saving={vitalsSaving}
          onSave={submitVitals}
          onCancel={cancelVitalsEdit}
          error={vitalsError}
        />
        {vitalsBodyEl}
      </section>

      {/* Growth chart (PROD-1538, PROD-2044) - paediatric only; the page gates by age (<=19y,
          Flutter's own gate) + known sex and passes the node, so this renders nothing for adults.
          Sits right after Vitals. NO card and NO section header: the DS chart carries Flutter's own
          header row (GROWTH CHART / age-domain links / Collapse / print), so wrapping it repeats the
          title and adds a frame Flutter does not draw (`growth_chart.dart:1226-1300`). */}
      {growthChart && <section aria-label="Growth chart">{growthChart}</section>}

      {deviceData}

      {/* Chief complaint — LIVE quick-capture (PROD-1588, Samer live-review verdict 2026-07-23).
          Typing feeds the CC RAG suggestions (AISearch/Search); chips toggle + "+ Create new"
          persist via CreateChiefComplaint; hydration comes from GetPatientComplaint. The host
          owns all data; `chiefComplaintDeferred` remains ONLY for the no-encounter-yet window
          (the POST needs the encounter guid) — honest-absent, not "coming soon". */}
      {/* CHIEF COMPLAINT — the design's card, rendered EXACTLY: eyebrow, the 38px search pill
          with its magnifier, the trailing 32px pencil, and the hint line. The DS
          ChiefComplaintEditor is deliberately NOT used here (Andrew 2026-08-08).
          FUNCTION PRESERVED: typing still drives the RAG suggestions and "+ Create new" still
          persists — those render BELOW the field and only when they have content, so the block
          at rest is exactly the design. The pencil opens the same free-text commit path. */}
      {!hidePreVisitCc && (
      <section
        // Published Flutter card (PROD-1549, 1920 / zoom 100 % / DPR 1): 112 tall, 1px border and
        // no shadow; label cap 20px below the top edge and 19px in; the 38px field 45px below the
        // top, ending 55px before the right border with the pencil centred 23px inside it.
        className={designV2 ? CHIEF_COMPLAINT_CARD_CLASS_V2 : CHIEF_COMPLAINT_CARD_CLASS}
        aria-label="Chief complaint"
      >
        {/* Switch ON: SYSTEM-RULES §2 — Chief Complaint is Subjective history, so its label takes
            the `history` wayfinding ink. OFF: today's brand-depth section label. */}
        <span className={designV2 ? V2_EYEBROW_HISTORY : sectionLabelClass}>Chief Complaint</span>
        {ccBodyEl}
      </section>
      )}

      {/* Special Form (submitted QR) — PROD-1251. Hidden when no questionnaire was submitted. */}
      {/* Notes from Nurse (E2 frame "Note" block, between Chief Complaint and the med sections) —
          hidden until the risk-assessment feed lands (see NurseNotesSection header). */}
      {nurseNotesNode ?? <NurseNotesSection notes={nurseNotes} />}

      {/* [PROD-1395] The dietitian's Referral card: flag `referrals` + allied health + a referral on file, else nothing. */}
      <ReferralCardSection patientId={patientId} encounterRole={encounterRole} />

      <SubmittedQuestionnaireSection form={submittedForm} error={submittedFormError} patientFirstName={patientFirstName} />

      {/* Previous Notes (follow-up) — Flutter body() places this after the questionnaire, before the
          admin-request blocks. Hidden when empty. (PROD-1523) */}
      <PreviousNotesSection notes={previousNotes ?? []} onView={onViewPreviousNote} />

      {/* Medication Administration Requests — Flutter body() orders this before vaccination. Hidden
          when empty. (PROD-1524) */}
      <MedicationAdminRequests requests={medicationAdminRequests ?? []} onGive={onGiveMedication} />

      {/* Vaccination Administration Requests (PROD-1250) — Flutter body() places this just before
          activeMedication(). Hidden when there are no requests. */}
      <VaccineAdministrationRequests requests={vaccinationRequests ?? []} onAdminister={onAdministerVaccination} />

      {/* Active Medications (inline) — gap #7. ROUND 4 conformed the block CHROME to the E2
          pre-encounter frame region (node 1937:183737, raw codegen numbers):
            · title cluster gap 4 — heading Roboto Bold 12 uppercase ls .504, Scrips Title Blue →
              blue-700; reminder line Medium 12 lh 16 ls -.24, Icons Grey → text-hint
            · container: white card, Lines Light Grey border → border-default, radius 13, pad 16,
              gap 16.
          ROUND 5 (feed trace, PROD-1588): the round-4 claim "the summary projection doesn't carry
          the rich feeds" was corrected — Rx chip (isOrder) + Ordered/Added-by
          (administration.performerDetails) + dates DO ride GetPatientSummary (Flutter
          patient_medications.dart); Patient SIG / Dispense / Refills / Ordered-for ride
          GET /api/EPriscription/GetPrescriptionOrderDetails (Flutter FetchEPrescriptionEvent).
          Both feeds are merged in use-prescription-orders.ts and rendered through the DS
          MedicationActiveRow (the LOCKED PROD-1588 row molecule — Rx tag · SIG line · prescriber ·
          expandable order-detail block with Patient SIG / Ordered for / Ordered on-by /
          Dispense·Refills / dates) — no hand-built rich UI app-side.
          Taking / Not-taking: NO reconciliation-status endpoint exists server-side
          (Scrips.Patient EPriscriptionController @ f68b66db exposes only create/Update/PostRefill/
          PostStopMedication — traced 2026-07-21), and Flutter has no such flow either, so the DS
          row's reconciliation handlers are NOT passed (the row hides those buttons).
          HIDDEN WHEN EMPTY (2026-08-07): Flutter's activeMedication() returns Container() on an
          empty list (:994) and PreEncounter.dc.html doesn't draw the block at all — the previous
          "No active medications recorded." card was React-only chrome. The roadmap note that sat
          under the reminder line went with it: a clinical surface should not narrate what the
          server can't do yet. */}
      {showActiveMeds && (activeMedications ?? []).length > 0 && (
      <section
        aria-label="Active medications"
        className="flex flex-col gap-4 w-full"
      >
        <div className="flex flex-col gap-1">
          <h2
            // Switch ON: SYSTEM-RULES §2 — the `medications` wayfinding ink (the chart's
            // Medications category, the note's Plan), on the caps-label role.
            className={designV2 ? `m-0 leading-normal ${V2_EYEBROW_MEDICATIONS}` : ACTIVE_MEDS_TITLE_CLASS}
          >
            Active Medications
          </h2>
          <p className={designV2 ? ACTIVE_MEDS_REMINDER_CLASS_V2 : ACTIVE_MEDS_REMINDER_CLASS}>
            Remember to do medication reconciliation before you start the encounter.
          </p>
        </div>
        <div
          // PROD-1588 layering fix — same --shadow-2 elevation as the Chief Complaint card, above.
          className={designV2 ? ACTIVE_MEDS_CARD_CLASS_V2 : ACTIVE_MEDS_CARD_CLASS}
        >
          {activeMedRowsEl}
        </div>
      </section>
      )}
    </main>
  );
}

/** The reason's location mark: the appointment glyph map's one location set (Plus / Home / Video / Ban), 16, in the
 *  secondary ink the row sets (icon pass, 16 Oct; was the DS `appt-*` font tile). */
function ReasonLocationGlyph({ type }: { type: string }) {
  return createElement(locationGlyph(type), { className: 'size-4', 'aria-hidden': true });
}
